import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { CommonButtonRowWidget, CommonDateTimeFieldWidget, CommonMultiselectFieldWidget, CommonSelectFieldWidget, CommonTextFieldWidget, UtilityService } from '@eforall/common';
import { PersonStatusFlags } from '@interfaces';
import { PersonDialogDataService } from '../../person-dialog-data.service';
import type { PersonDialog } from '../../person.dialog';
import { PersonDemographicsForm } from './demographics.form';

@Component({
	selector: 'person-demographics-part',
	standalone: true,
	imports: [
		CommonSelectFieldWidget,
		CommonMultiselectFieldWidget,
		CommonDateTimeFieldWidget,
		CommonTextFieldWidget,
		CommonButtonRowWidget,
		FormsModule
	],
	templateUrl: './demographics.part.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: './demographics.part.scss'
})

export class DemographicsPart implements OnInit, AfterViewInit {

	// Injecting parent dialog reference so this nested part can close the same open Material dialog from its local Close button
	public dialogRef = inject(MatDialogRef<PersonDialog>);

	private personDialogDataService = inject(PersonDialogDataService);
	private util = inject(UtilityService);

	public readonly demographicsForm = new PersonDemographicsForm();
	public saveButton = viewChild.required<ElementRef<HTMLButtonElement>>('demographicsSaveBtn');

	/**
	 * List of available demographic status flags
	 * Used to render checkboxes in the template
	*/
	readonly STATUS_FLAGS: Array<keyof PersonStatusFlags> = [
		'hasOptOutDemographics',
		'isHandicapped',
		'isDeceased'
	] as const;

	ngOnInit(): void {
		this.setInitialValues();
	}

	ngAfterViewInit(): void {
		this.demographicsForm.form.setSubmitButton(
			this.saveButton(),
			async (values, changed: string[]) => {
				const { hasOptOutDemographics, isHandicapped, isDeceased } = this.demographicsForm.personStatusFlags();
				const birthDate = this.util.date.getBirthMonthDayAndYearFromUTC(values.birthday);
				await this.personDialogDataService.updateDemographics(
					{
						gender: values.gender as "M" | "F" | "N" | "O" | "OptedOut" ?? undefined,
						genderOther: values.genderOther || undefined,
						races: values.races || undefined,
						hispanic: values.hispanic,
						birthYear: birthDate.birthYear,
						birthDay: birthDate.birthDay,
						veteran: values.veteran,
						nativeEnglish: values.nativeEnglish,
						nativeSpanish: values.nativeSpanish,
						hasOptOutDemographics,
						isHandicapped,
						isDeceased,
					}
				);
			}
		);
	}

	setInitialValues() {
		const personDetails = this.personDialogDataService.personDetails();
		if (!personDetails) return;
		this.demographicsForm.setValues(personDetails);
	}

	updateStatusFlags(type: keyof PersonStatusFlags, flag: boolean) {
		this.demographicsForm.updatePersonStatusFlags({ [type]: flag })
	}

	showViewMode() {
		this.personDialogDataService.toEdit.set(false);
	}

	onClose() {
		this.dialogRef.close();
	}

}